import { useState, useMemo, Fragment } from "react";
import { Table, ProgressBar } from "react-bootstrap";
import moment from "moment";
import { MdNextPlan } from "react-icons/md";
import { useParams } from "react-router-dom";
import { IoIosRefreshCircle } from "react-icons/io";
import { trellisPalette } from "components/custom/analytics/palettes";
import { Button } from "components/core/basic/Button";
import { useStartWorkflow } from "./api/useStartWokflow";
import { useWorkflows } from "./api/useWorkflows";
import type { ReportStatus, Workflow } from "../../zodios/apiDefinitions/workflows";
import { useWorkflowState } from "./api/useWorkflowState";
import { HStack, VStack, Wrap } from "../../../styled-system/jsx";
import { useWorkflowProgress } from "./api/useWorkflowProgress";
import { PageContainer } from "../../components/core/basic/PageContainer";

const JOB_STATUS = {
  0: "PENDING",
  1: "STARTING",
  2: "SUCCESS",
  3: "FAILURE",
  4: "UNKNOWN_STATUS",
};

const ReportRow = ({
  plan_reports,
  reportType,
}: {
  plan_reports: [string, Workflow["task_context"]["pending_reports"][string]];
  reportType: string;
}) => {
  const getDate = (
    executionOptions: Workflow["task_context"]["pending_reports"][string][number]["pending_report"]["payload"]["execution_options"],
  ) => {
    if (!executionOptions) {
      return "N/A";
    }

    if (executionOptions.reportDate) {
      return executionOptions.reportDate.replace(/(\d{4})(\d{2})(\d{2})/, "$1-$2-$3");
    }

    if (executionOptions.startDate && executionOptions.endDate) {
      return `${executionOptions.startDate} - ${executionOptions.endDate}`;
    }

    if (executionOptions.dataStartTime && executionOptions.dataEndTime) {
      return `${executionOptions.dataStartTime} - ${executionOptions.dataEndTime}`;
    }

    return "N/A";
  };

  if (!reportType) {
    return null;
  }

  // sort plan_reports[1] by report.pending_report.wait_until
  plan_reports[1].sort((a, b) => (a.pending_report.wait_until ?? "").localeCompare(b.pending_report.wait_until ?? ""));

  return plan_reports[1].map((report, index) => {
    return report.pending_report.report_type === reportType ?
        <tr key={index}>
          <td className="text-center">
            <a
              href={`https://fawcett.growthvectors.io/admin/backend/plan/${plan_reports[0]}/change/`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {plan_reports[0]}
            </a>
          </td>
          <td>
            {report.pending_report.report_type}
            <div>
              <small>{report.pending_report.id}</small>
            </div>
          </td>
          <td className="text-center">
            <a
              href={`https://fawcett.growthvectors.io/admin/backend/credential/${report.pending_report.credential}/change/`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {report.pending_report.credential}
            </a>
          </td>
          <td className="text-center">
            <div
              style={{ cursor: "pointer" }}
              onClick={() => {
                alert(report.pending_report.payload.marketplace_ids?.join("\n"));
              }}
            >
              {report.pending_report.payload.marketplace_ids?.length}
            </div>
          </td>
          <td className="text-center">{getDate(report.pending_report.payload.execution_options)}</td>
          <td className="text-center">
            {report.pending_report.status}
            <div>
              <small>{report.pending_report.payload.report_id}</small>
            </div>
          </td>
          <td className="text-center">
            {report.pending_report.status !== "S" && report.pending_report.wait_until ?
              `${moment(report.pending_report.wait_until).diff(moment(), "minutes")} mins`
            : "n/a"}
          </td>
          <td className="text-center">
            {report.pending_report.warehouse?.id && (
              <a
                href={`https://fawcett.growthvectors.io/api/reporting/warehouse/${report.pending_report.warehouse.id}/`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {report.pending_report.warehouse.id}
              </a>
            )}

            {!report.pending_report.warehouse?.id && "N/A"}
          </td>
        </tr>
      : null;
  });
};

const ReportStatus = ({
  pendingReport,
  setReportType,
}: {
  pendingReport: Workflow["task_context"]["pending_reports"];
  setReportType: (reportType: string) => void;
}) => {
  const reports: Record<string, { [key in ReportStatus]: number }> = {};

  Object.entries(pendingReport).forEach((plan_reports) => {
    plan_reports[1].forEach((report) => {
      const reportType = report.pending_report.report_type;
      const status = report.pending_report.status;
      if (!reports[reportType]) {
        reports[reportType] = { P: 0, S: 0, F: 0, K: 0, E: 0, "N/A": 0 };
      }

      reports[reportType][status] += 1;
    });
  });

  return (
    <Table striped bordered hover>
      <thead>
        <tr>
          <th>Report</th>
          <th>P</th>
          <th>S</th>
          <th>F</th>
          <th>K</th>
          <th>E</th>
          <th>N/A</th>
          <th> </th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(reports).map((report, index) => (
          <tr key={index}>
            <td>{report[0]}</td>
            <td className="text-center">{report[1].P}</td>
            <td className="text-center">{report[1].S}</td>
            <td className="text-center">{report[1].F}</td>
            <td className="text-center">{report[1].K}</td>
            <td className="text-center">{report[1].E}</td>
            <td className="text-center">{report[1]["N/A"]}</td>
            <td className="text-center">
              <span style={{ cursor: "pointer" }} onClick={() => setReportType(report[0])}>
                <MdNextPlan />
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

const Workflows = () => {
  const { workflow_id: workflowIdParam = null } = useParams<{ workflow_id?: string }>();
  const [workflowId, setWorkflowId] = useState(workflowIdParam);
  const [reportType, setReportType] = useState<string | null>(null);

  const startWorkflowMutation = useStartWorkflow({
    onSuccess() {
      alert("Workflow started");
    },
  });

  const workflowsQuery = useWorkflows({ page_size: 5000 }, { refetchInterval: 600000 });
  const workflow = workflowsQuery.data?.find((d) => d.id === workflowId);

  const sortedTasks = useMemo(() => {
    if (!workflow) return [];

    return Object.entries(workflow.task_context.tasks)
      .map(([key, value]) => {
        return { ...value, name: key };
      })
      .sort((a, b) => {
        if (a.start_time < b.start_time) {
          return -1;
        }
        if (a.start_time > b.start_time) {
          return 1;
        }
        return 0;
      });
  }, [workflow]);

  const stateQuery = useWorkflowState(workflow?.tracking_id);
  const progressQuery = useWorkflowProgress(workflow?.tracking_id);

  return (
    <PageContainer>
      <VStack alignItems={"stretch"}>
        <HStack justifyContent={"space-between"} fontSize={"medium"}>
          <span className="fs-3">Workflows</span>
          <HStack>
            Refreshed:{" "}
            {workflowsQuery.dataUpdatedAt && !workflowsQuery.isFetching ?
              moment(workflowsQuery.dataUpdatedAt).format("YYYY-MM-DD HH:mm:ss")
            : workflowsQuery.isFetching ?
              "In flight"
            : "never"}
            <IoIosRefreshCircle
              style={{ cursor: "pointer", color: trellisPalette[0] }}
              onClick={async () => {
                await workflowsQuery.refetch();
              }}
            />
          </HStack>
        </HStack>
        <Wrap>
          <Button
            onPress={() => {
              startWorkflowMutation.mutate("sp");
            }}
            isPending={startWorkflowMutation.isPending}
          >
            Start SP API Workflow
          </Button>
          <Button
            onPress={() => {
              startWorkflowMutation.mutate("ads");
            }}
            isPending={startWorkflowMutation.isPending}
          >
            Start SP Ads Workflow
          </Button>
          <Button
            onPress={() => {
              startWorkflowMutation.mutate("both");
            }}
            isPending={startWorkflowMutation.isPending}
          >
            Start OnBoarding (SP + Ads) Workflow
          </Button>
          <Button
            onPress={() => {
              startWorkflowMutation.mutate("pricing");
            }}
            isPending={startWorkflowMutation.isPending}
          >
            Start Pricing Workflow
          </Button>
        </Wrap>

        {(() => {
          switch (workflowsQuery.status) {
            case "pending":
              return <div>Loading...</div>;
            case "error":
              return <div>Error loading workflows</div>;
            case "success":
              return (
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Tracking ID</th>
                      <th>Status</th>
                      <th>Created At</th>
                      <th>Updated At</th>
                      <th>&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workflowsQuery.data.map((workflow, index) => (
                      <tr key={index}>
                        <td>
                          <a
                            href={`https://api.growthvectors.io/admin/api/workflow/${workflow.id}/change/`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {workflow.id}
                          </a>
                        </td>
                        <td>{workflow.tracking_id}</td>
                        <td className="text-center">{workflow.status}</td>
                        <td className="text-center">{moment(workflow.created_at).format("YYYY-MM-DD HH:mm:ss")}</td>
                        <td className="text-center">{moment(workflow.updated_at).format("YYYY-MM-DD HH:mm:ss")}</td>
                        <td className="text-center">
                          <span
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              if (workflow.id === workflowId) {
                                setWorkflowId(null);
                              } else {
                                setWorkflowId(workflow.id);
                              }
                              setReportType(null);
                            }}
                          >
                            <MdNextPlan />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              );
          }
        })()}

        {workflow && (
          <>
            <span className="fs-3">{workflow.id}</span>

            {!!progressQuery.data?.progress && (
              <ProgressBar
                now={(progressQuery.data.progress[0] / progressQuery.data.progress[1]) * 100}
                variant="success"
                style={{ height: "10px" }}
              />
            )}

            <div className="fs-4 pt-4">Tasks</div>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Task Name</th>
                  <th>Started At</th>
                  <th>Job #</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {sortedTasks.map((task, index) => (
                  <Fragment key={index}>
                    <tr>
                      <td>{task.name}</td>
                      <td className="text-center">
                        {task.start_time ? moment(task.start_time).format("YYYY-MM-DD hh:mm:ss") : ""}
                      </td>
                      <td className="text-center">{task.job_id}</td>
                      <td className="text-center">
                        {stateQuery.data &&
                          task.job_id &&
                          stateQuery.data.job_states[task.job_id]?.status !== undefined && (
                            <>
                              <span>{JOB_STATUS[stateQuery.data.job_states[task.job_id]!.status]}</span>
                              &nbsp;- (<span>{stateQuery.data.job_states[task.job_id]?.status}</span>)
                            </>
                          )}
                      </td>
                    </tr>
                    {stateQuery.data && task.job_id && stateQuery.data.job_states[task.job_id]?.status === 3 && (
                      <tr>
                        <td colSpan={4}>
                          <pre>{stateQuery.data.job_states[task.job_id]?.exception as string}</pre>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </Table>

            <div className="fs-4 pt-4">Product Download Tasks</div>

            {/* wrap table with a scrollable div with 300px height */}
            <div style={{ height: "300px", overflowY: "auto" }}>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Task</th>
                    <th>Completed At</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(workflow.task_context.download_listings_tasks).map((task, index) => (
                    <tr key={index}>
                      <td>{task[0]}</td>
                      <td className="text-center">{task[1] ? moment(task[1]).format("YYYY-MM-DD hh:mm:ss") : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <div className="fs-4 pt-4">Reports</div>
            <ReportStatus pendingReport={workflow.task_context.pending_reports} setReportType={setReportType} />
            {reportType && (
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Plan ID</th>
                    <th>Report</th>
                    <th>Credential</th>
                    <th>Marketplace</th>
                    <th>Dates</th>
                    <th>Status</th>
                    <th>Wait Until</th>
                    <th>Warehouse ID</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(workflow.task_context.pending_reports).map((plan_reports, index) => (
                    <Fragment key={index}>
                      <ReportRow plan_reports={plan_reports} reportType={reportType} />
                    </Fragment>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </VStack>
    </PageContainer>
  );
};

export default Workflows;
